import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
    fetchCompanyDetails,
    updateCompanyDetails,
    fetchPartners,
    addPartner
} from '../../../../store/startup/company/companySlice';
import {
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Button,
    FormHelperText,
    Paper,
    IconButton,
    Tooltip,
    Grid,
    useTheme,
    Divider,
    alpha,
    Alert
} from '@mui/material';
import { HelpOutline } from '@mui/icons-material';
import PartnerAccordion from './PartnerAccordion';
import cities from '../../../../assets/json/cities.json';
import districts from '../../../../assets/json/districts.json';

const Company = () => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const { startupId } = useParams();
    const { details, partners, loading } = useSelector(state => state.company || {});
    const [formData, setFormData] = useState({
        city: '',
        district: '',
        capital: '',
        address: ''
    });
    const [newPartners, setNewPartners] = useState([{
        isNew: true,
        startupId: startupId,
        name: '',
        title: '',
        education: '',
        gpa: '',
        cvPath: '',
        countryCode: 'TR',
        shareAmount: '',
        shareRatio: '',
        voteRatio: '',
        privilege: '',
        relation: '',
        experience: '',
        proficiency: ''
    }]);
    const [availableDistricts, setAvailableDistricts] = useState([]);

    // Add error states
    const [errors, setErrors] = useState({
        city: false,
        district: false,
        capital: false,
        address: false
    });

    useEffect(() => {
        dispatch(fetchCompanyDetails(startupId));
        dispatch(fetchPartners(startupId));
    }, [dispatch, startupId]);

    useEffect(() => {
        if (details) {
            setFormData({
                city: details.city || '',
                district: details.district || '',
                capital: details.capital || '',
                address: details.address || ''
            });
        }
    }, [details]);

    useEffect(() => {
        if (formData.city) {
            const selectedCity = cities.find(city => city.name === formData.city);
            if (selectedCity) {
                const cityDistricts = districts.filter(district => district.il_id === selectedCity.id);
                setAvailableDistricts(cityDistricts);
            } else {
                setAvailableDistricts([]);
            }
        } else {
            setAvailableDistricts([]);
        }
    }, [formData.city]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        // Clear error when user starts typing
        setErrors(prev => ({
            ...prev,
            [name]: false
        }));

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSave = async () => {
        // Reset errors
        setErrors({
            city: false,
            district: false,
            capital: false,
            address: false
        });

        // Validate inputs
        const newErrors = {
            city: !formData.city.trim(),
            district: !formData.district.trim(),
            capital: !formData.capital.toString().trim(),
            address: !formData.address.trim()
        };

        // Check if any errors exist
        if (Object.values(newErrors).some(error => error)) {
            setErrors(newErrors);
            return;
        }

        try {
            await dispatch(updateCompanyDetails({
                startupId,
                ...formData
            })).unwrap();
        } catch (error) {
            console.error('Failed to update company details:', error);
        }
    };

    const handleNewPartnerSaved = () => {
        setNewPartners([]);
        dispatch(fetchPartners(startupId));
    };

    return (
        <Box >
            <Box sx={{ display: 'flex', width: '100%' }}>
                <Box sx={{ flexGrow: 1, width: '80%' }}>
                    <Typography variant="h5" sx={{ mb: 2 }}>
                        Kurulacak Şirket Hakkında
                    </Typography>
                </Box>
                <Divider sx={{ marginBottom: 3 }} />
            </Box>

            <Box>
                <Alert
                    severity="info"
                    sx={{
                        mb: 3,
                        bgcolor: alpha(theme.palette.info.main, 0.08),
                        border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                        borderRadius: 2
                    }}
                >
                    Şirket bilgilerini eksiksiz ve doğru bir şekilde doldurmanız önemlidir. Bu bilgiler yatırımcılar tarafından değerlendirilecektir.
                </Alert>

                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth error={errors.city}>
                            <InputLabel>İl</InputLabel>
                            <Select
                                name="city"
                                value={formData.city}
                                onChange={handleChange}
                                label="İl"
                            >
                                <MenuItem value="">Seçiniz</MenuItem>
                                {cities.map(city => (
                                    <MenuItem key={city.id} value={city.name}>
                                        {city.name}
                                    </MenuItem>
                                ))}
                            </Select>
                            {errors.city && (
                                <FormHelperText>İl seçimi zorunludur</FormHelperText>
                            )}
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth error={errors.district}>
                            <InputLabel>İlçe</InputLabel>
                            <Select
                                name="district"
                                value={formData.district}
                                onChange={handleChange}
                                label="İlçe"
                                disabled={!formData.city}
                            >
                                <MenuItem value="">Seçiniz</MenuItem>
                                {availableDistricts.map(district => (
                                    <MenuItem key={district.id} value={district.name}>
                                        {district.name}
                                    </MenuItem>
                                ))}
                            </Select>
                            {errors.district && (
                                <FormHelperText>İlçe seçimi zorunludur</FormHelperText>
                            )}
                        </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Toplanan Fon Sonrası Elde Edilecek Sermaye"
                            name="capital"
                            value={formData.capital}
                            onChange={handleChange}
                            error={errors.capital}
                            helperText={errors.capital ? "Sermaye alanı zorunludur" : ""}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '&:hover fieldset': {
                                        borderColor: theme.palette.primary.main,
                                    },
                                },
                            }}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Adres"
                            name="address"
                            value={formData.address}
                            onChange={handleChange}
                            multiline
                            rows={4}
                            error={errors.address}
                            helperText={errors.address ? "Adres alanı zorunludur" : ""}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '&:hover fieldset': {
                                        borderColor: theme.palette.primary.main,
                                    },
                                },
                            }}
                        />
                    </Grid>
                </Grid>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={loading}
                        sx={{
                            bgcolor: theme.palette.primary.main,
                            '&:hover': {
                                bgcolor: theme.palette.primary.dark,
                            },
                        }}
                    >
                        {loading ? 'Kaydediliyor...' : 'Kaydet'}
                    </Button>
                </Box>
            </Box>
            <Divider sx={{ marginBottom: 3 }} />
            <Box sx={{ display: 'flex', width: '100%' }}>
                <Box sx={{ flexGrow: 1, width: '80%' }}>
                    <Typography variant="h5" sx={{ mb: 2 }}>
                        Fonlama Sonrası Kurucu Ortaklar
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'right', justifyContent: 'right', width: '20%', height: '1%' }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            const emptyPartner = {
                                isNew: true,
                                startupId: startupId,
                                name: '',
                                title: '',
                                education: '',
                                gpa: '',
                                cvPath: '',
                                countryCode: 'TR',
                                shareAmount: '',
                                shareRatio: '',
                                voteRatio: '',
                                privilege: '',
                                relation: '',
                                experience: '',
                                proficiency: ''
                            };
                            setNewPartners([...newPartners, emptyPartner]);
                        }}
                        sx={{
                            bgcolor: theme.palette.primary.main,
                            '&:hover': {
                                bgcolor: theme.palette.primary.dark,
                            },
                        }}
                    >
                        Yeni Partner Ekle
                    </Button>
                </Box>
                <Divider sx={{ marginBottom: 3 }} />
            </Box>

            <Box>
                {partners?.map((partner) => (
                    <PartnerAccordion
                        key={partner.id}
                        partner={partner}
                        startupId={startupId}
                    />
                ))}

                {newPartners.map((partner, index) => (
                    <PartnerAccordion
                        key={`new-${index}`}
                        partner={partner}
                        startupId={startupId}
                        onSaved={handleNewPartnerSaved}
                    />
                ))}
            </Box>
        </Box>
    );
};

export default Company;